import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import ListIcon from '@mui/icons-material/List';
import { SvgIconProps } from '@mui/material';
import { LinkProtocolOptions } from '@tiptap/extension-link';
import { type Editor } from '@tiptap/react';
import React, { useCallback, useEffect } from 'react';
export const DEFAULT_MAX_CHAR_LIMIT = 5000;

type ToolbarButton = {
    id: string;
    icon: React.ComponentType<SvgIconProps>;
    command: (editor: Editor) => void;
    isActive?: (editor: Editor) => boolean;
};

type CtxType = {
    defaultValidate: (url: string) => boolean;
    protocols: Array<LinkProtocolOptions | string>;
    defaultProtocol: string;
};

export const LinkConfig = {
    openOnClick: false,
    enableClickSelection: true,
    autolink: true,
    defaultProtocol: 'https',
    protocols: ['http', 'https'],
    isAllowedUri: (url: string, ctx: CtxType): boolean => {
        try {
            // construct URL
            const parsedUrl = url.includes(':') ? new URL(url) : new URL(`${ctx.defaultProtocol}://${url}`);

            // use default validation
            if (!ctx.defaultValidate(parsedUrl.href)) {
                return false;
            }

            // disallowed protocols
            const disallowedProtocols = ['ftp', 'file'];
            const protocol = parsedUrl.protocol.replace(':', '');

            if (disallowedProtocols.includes(protocol)) {
                return false;
            }

            // only allow protocols specified in ctx.protocols
            const allowedProtocols = ctx.protocols.map(p => (typeof p === 'string' ? p : p.scheme));

            if (!allowedProtocols.includes(protocol)) {
                return false;
            }

            // all checks have passed
            return true;
        } catch {
            return false;
        }
    },
};

// Custom hook for managing toolbar state
export const useToolbarState = (
    editor: Editor | null
): { formats: string[]; setFormats: (formats: string[]) => void } => {
    const [formats, setFormats] = React.useState<string[]>([]);

    const updateFormats = useCallback(() => {
        if (!editor) return;

        const activeFormats: string[] = [];
        const formatChecks = [
            { format: 'bold', check: () => editor.isActive('bold') },
            { format: 'italic', check: () => editor.isActive('italic') },
            { format: 'underline', check: () => editor.isActive('underline') },
            { format: 'link', check: () => editor.isActive('link') },
            { format: 'bulletList', check: () => editor.isActive('bulletList') },
            { format: 'orderedList', check: () => editor.isActive('orderedList') },
        ];

        formatChecks.forEach(({ format, check }) => {
            if (check()) {
                activeFormats.push(format);
            }
        });

        setFormats(activeFormats);
    }, [editor]);

    useEffect(() => {
        if (!editor) return;

        editor.on('update', updateFormats);
        editor.on('selectionUpdate', updateFormats);

        return () => {
            editor.off('update', updateFormats);
            editor.off('selectionUpdate', updateFormats);
        };
    }, [editor, updateFormats]);

    return { formats, setFormats };
};

// Custom hook for link handling
export const useLinkHandler = (editor: Editor | null): (() => void) => {
    return useCallback(() => {
        if (!editor) return;

        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl);

        if (url === null) return; // cancelled

        if (url === '') {
            // Remove link
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }

        // Set/update link
        try {
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        } catch (error) {
            console.error('Error setting link:', error);
        }
    }, [editor]);
};

// Toolbar button definitions
export const getToolbarButtons = (setLink: () => void): ToolbarButton[] => [
    {
        id: 'bold',
        icon: FormatBoldIcon,
        command: editor => editor.chain().focus().toggleBold().run(),
    },
    {
        id: 'italic',
        icon: FormatItalicIcon,
        command: editor => editor.chain().focus().toggleItalic().run(),
    },
    {
        id: 'underline',
        icon: FormatUnderlinedIcon,
        command: editor => editor.chain().focus().toggleUnderline().run(),
    },
    {
        id: 'link',
        icon: InsertLinkIcon,
        command: setLink,
    },
    {
        id: 'bulletList',
        icon: ListIcon,
        command: editor => editor.chain().focus().toggleBulletList().run(),
    },
    {
        id: 'orderedList',
        icon: FormatListNumberedIcon,
        command: editor => editor.chain().focus().toggleOrderedList().run(),
    },
];
